import { Badge } from 'features/catalog/useBadges';
import {
  getJourneyCompletionPercentage,
  isJourneyCompleted,
  JOURNEYS,
  type BadgeOwnership,
  type Journey,
} from 'features/catalog/badge-journeys.ts';
import {
  RARITY_ORDER,
  rarityDividers,
  rarityPoints,
  type RarityName,
  type RarityPoints,
} from 'features/catalog/rarity-config.ts';

export {
  BOARD_STAT_LEGENDARY_LEAD,
  boardStatTone,
  DECK_STAT_TOTAL_PER_SLOT,
  deckStatTotalTone,
  RARITY_ORDER,
  rarityDividers,
  rarityGhostProfiles,
  rarityNameForStatScore,
  rarityPoints,
  sortBadgeHashByRarity,
  STAT_SCORE_RARITY,
  IN_PLAY_DIVISION_MODIFIER,
  statTotalLegendaryAt,
  statTotalTone,
  TRIO_STAT_TOTAL_AT,
  trioStatTotalTone,
} from 'features/catalog/rarity-config.ts';
export type {
  BoardStatTone,
  DeckStatTotalTone,
  RarityName,
  RarityPoints,
  StatScore,
  StatScoreTone,
  StatTotalTone,
} from 'features/catalog/rarity-config.ts';

export const isRarityName = (value: string): value is RarityName => RARITY_ORDER.some((rarity) => rarity === value);

export const asRarityName = (rarityName: string): RarityName => (isRarityName(rarityName) ? rarityName : 'common');

export const RARITY_TIER_LABELS: Record<RarityName, string> = {
  mythic: 'Mythic',
  legendary: 'Legendary',
  rare: 'Rare',
  common: 'Common',
};

/** Display rows for each rarity — scores always come from `rarityPoints`. */
export const RARITY_TIERS = RARITY_ORDER.map((key) => ({
  key,
  score: rarityPoints[key],
  label: RARITY_TIER_LABELS[key],
}));

/** Map from numeric rarity points to display name — derived from `rarityPoints`. */
export const RARITY_SCORE_TO_NAME: Record<number, RarityName> = {
  [rarityPoints.mythic]: 'mythic',
  [rarityPoints.legendary]: 'legendary',
  [rarityPoints.rare]: 'rare',
  [rarityPoints.common]: 'common',
};

/** Converts a rarity name to its numeric point value. */
export const getRarityScoreFromName = (rarityName: string): RarityPoints => rarityPoints[asRarityName(rarityName)];

/** Converts numeric rarity points to a display name. */
export const getRarityNameFromScore = (rarityScore: number): RarityName =>
  RARITY_SCORE_TO_NAME[rarityScore] ?? 'common';

/** Returns the sort index of a rarity name (lower = rarer). Unknown names sort last. */
export const rarityOrderIndex = (rarityName: string): number => {
  const idx = RARITY_ORDER.indexOf(rarityName as RarityName);
  return idx === -1 ? RARITY_ORDER.length : idx;
};

/** Sort comparator — lower badgeScore first (rarest / least-claimed among eligible). */
export const compareBadgesByScoreAsc = (a: Badge, b: Badge): number => (a.badgeScore ?? 0) - (b.badgeScore ?? 0);

/** Sort comparator — higher badgeScore first (most claimed among eligible / easiest). */
export const compareBadgesByScoreDesc = (a: Badge, b: Badge): number => (b.badgeScore ?? 0) - (a.badgeScore ?? 0);

/**
 * Within a group, push special badges to the end (even when claimed):
 * 1. `forceSortLast` — after normal badges
 * 2. `banSelfClaim` — after everything, including `forceSortLast`
 */
export const compareForceSortLast = (
  a: Pick<Badge, 'forceSortLast' | 'banSelfClaim'>,
  b: Pick<Badge, 'forceSortLast' | 'banSelfClaim'>
): number => {
  const banDiff = Number(!!a.banSelfClaim) - Number(!!b.banSelfClaim);
  if (banDiff !== 0) return banDiff;
  return Number(!!a.forceSortLast) - Number(!!b.forceSortLast);
};

/**
 * Returns the public URL for a rarity tier image (e.g. rarities/mythic.png).
 * Accepts the numeric rarity score from `rarityPoints`.
 */
export const getRarityImageUrl = (rarityScore: number): string => {
  const rarityName = getRarityNameFromScore(rarityScore);
  return `${process.env.PUBLIC_URL}/images/rarities/${rarityName}.png`;
};

/** Public URL for a rarity tier image by display name (common | rare | legendary | mythic). */
export const getRarityImageUrlByName = (rarityName: string): string =>
  `${process.env.PUBLIC_URL}/images/rarities/${rarityName}.png`;

/** Rarity counts grouped by tier. */
export interface RarityCounts {
  mythic: number;
  legendary: number;
  rare: number;
  common: number;
}

/**
 * Computes rarity counts for a list of badge IDs using the badges map.
 * Returns zeroed counts if no badges match.
 */
export const computeRarityCounts = (badgeIDs: readonly string[], badgesMap: Map<string, Badge>): RarityCounts => {
  const counts: RarityCounts = { mythic: 0, legendary: 0, rare: 0, common: 0 };
  badgeIDs.forEach((id) => {
    const badge = badgesMap.get(id);
    if (badge) counts[asRarityName(badge.rarityName)]++;
  });
  return counts;
};

/**
 * Counts completed journeys by rarity tier for a given set of badge IDs.
 * Each completed journey contributes +1 to its rarity tier.
 */
export const computeJourneyRarityCounts = (badgeIDs: string[] | ReadonlySet<string>): RarityCounts => {
  const counts: RarityCounts = { mythic: 0, legendary: 0, rare: 0, common: 0 };
  const owned = Array.isArray(badgeIDs) ? new Set(badgeIDs) : badgeIDs;
  for (const journey of JOURNEYS) {
    if (!isJourneyCompleted(journey, owned)) continue;
    counts[asRarityName(journey.rarity)]++;
  }
  return counts;
};

/**
 * Unique journeys counted by each journey’s rarity (not the badges that finish them).
 * Two OR-picks for the same legendary journey still count as 1 legendary.
 */
export const countUniqueJourneyRarities = (journeys: readonly Pick<Journey, 'id' | 'rarity'>[]): RarityCounts => {
  const counts: RarityCounts = { mythic: 0, legendary: 0, rare: 0, common: 0 };
  const seen = new Set<string>();
  for (const journey of journeys) {
    if (seen.has(journey.id)) continue;
    seen.add(journey.id);
    counts[asRarityName(journey.rarity)]++;
  }
  return counts;
};

/** Profiles with fewer badges than this are omitted from claimed-%. */
export const MIN_BADGES_FOR_CLAIM_RATE = 5;

export const profileCountsTowardClaimRate = (profile: { badgesIDs?: readonly string[] }): boolean =>
  (profile.badgesIDs?.length ?? 0) >= MIN_BADGES_FOR_CLAIM_RATE;

/**
 * Per-journey completion counts across profiles — same numerator the stats page
 * uses for journey claimed % (`completions / profile count`).
 */
export const countJourneyCompletionsById = (profiles: Iterable<{ badgesIDs: string[] }>): Map<string, number> => {
  const completions = new Map(JOURNEYS.map((journey) => [journey.id, 0]));
  for (const profile of profiles) {
    const owned = new Set(profile.badgesIDs);
    for (const journey of JOURNEYS) {
      if (!isJourneyCompleted(journey, owned)) continue;
      completions.set(journey.id, (completions.get(journey.id) ?? 0) + 1);
    }
  }
  return completions;
};

/** Global completions divided by `rarityDividers` for that journey’s rarity. */
export const journeyClaimScore = (rarity: string, completions: number): number =>
  completions / rarityDividers[asRarityName(rarity)];

/** Lowest claim score first (coolest), then name — same order as completed journeys on a profile. */
export const compareJourneysByClaimScoreAsc = (
  a: Pick<Journey, 'id' | 'name' | 'rarity'>,
  b: Pick<Journey, 'id' | 'name' | 'rarity'>,
  completionsById: Map<string, number>
): number => {
  const diff =
    journeyClaimScore(a.rarity, completionsById.get(a.id) ?? 0) -
    journeyClaimScore(b.rarity, completionsById.get(b.id) ?? 0);
  if (diff !== 0) return diff;
  return a.name.localeCompare(b.name);
};

/**
 * Highest claim score first (`completions / rarityDividers`), then name.
 */
export const sortJourneysByClaimRate = <T extends { id: string; name?: string; rarity: string }>(
  journeys: readonly T[],
  completionsById: Map<string, number>
): T[] =>
  [...journeys].sort((a, b) => {
    const diff =
      journeyClaimScore(b.rarity, completionsById.get(b.id) ?? 0) -
      journeyClaimScore(a.rarity, completionsById.get(a.id) ?? 0);
    if (diff !== 0) return diff;
    return (a.name ?? a.id).localeCompare(b.name ?? b.id);
  });

/** Full catalog ordered by global completion count. */
export const getJourneysSortedByClaimRate = (profiles: Iterable<{ badgesIDs: string[] }>): typeof JOURNEYS =>
  sortJourneysByClaimRate(JOURNEYS, countJourneyCompletionsById(profiles));

/**
 * Profile Journeys tab: completed first (lowest claim score = coolest first), then
 * unfinished last by this profile’s finished % (closest to done first). Name is the tie-break.
 */
export const sortJourneysForProfile = (
  journeys: readonly Journey[],
  badgeIDs: BadgeOwnership,
  completionsById: Map<string, number>
): Journey[] =>
  [...journeys].sort((a, b) => {
    const aDone = isJourneyCompleted(a, badgeIDs);
    const bDone = isJourneyCompleted(b, badgeIDs);
    if (aDone !== bDone) return aDone ? -1 : 1;
    if (aDone) return compareJourneysByClaimScoreAsc(a, b, completionsById);
    const progressDiff = getJourneyCompletionPercentage(b, badgeIDs) - getJourneyCompletionPercentage(a, badgeIDs);
    if (progressDiff !== 0) return progressDiff;
    return a.name.localeCompare(b.name);
  });

export const getJourneysSortedForProfile = (
  badgeIDs: BadgeOwnership,
  profiles: Iterable<{ badgesIDs: string[] }>
): Journey[] => sortJourneysForProfile(JOURNEYS, badgeIDs, countJourneyCompletionsById(profiles));

export const mergeRarityCounts = (a: RarityCounts, b: RarityCounts): RarityCounts => ({
  mythic: a.mythic + b.mythic,
  legendary: a.legendary + b.legendary,
  rare: a.rare + b.rare,
  common: a.common + b.common,
});
